import React from "react";
import type { PotentialFocus } from "../../types";
import type { Chip as ChipModel, EffortBand, Potential, Stage } from "./usePotential";

interface PotentialPanelProps {
  potential: Potential;
  focus: PotentialFocus | undefined;
}

// 0.55: lower reads as disabled; the whole panel must stay legible while one block is emphasised.
const DIMMED = 0.55;

const CHIP_TONE: Record<ChipModel["tone"], string> = {
  solid: "bg-abis-primary/10 text-abis-primary border-abis-primary/20",
  plain: "bg-gray-50 text-gray-600 border-gray-200",
  warn: "bg-abis-warning/10 text-abis-warning border-abis-warning/30",
  ok: "bg-abis-success/10 text-abis-success border-abis-success/30",
};

const STAGE_COLOR: Record<Stage, string> = {
  1: "bg-abis-error",
  2: "bg-abis-warning",
  3: "bg-abis-primary",
  4: "bg-abis-primary",
  5: "bg-abis-success",
};

const BAND_COLOR: Record<EffortBand, string> = {
  1: "bg-abis-success",
  2: "bg-abis-primary",
  3: "bg-abis-warning",
  4: "bg-abis-error",
};

function Chips({ chips, empty }: { chips: ChipModel[]; empty: string }) {
  if (chips.length === 0) {
    return <p className="text-[11px] text-gray-400 italic leading-snug">{empty}</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((chip, index) => (
        <span
          key={`${chip.label}-${index}`}
          className={`inline-block rounded-md border px-2 py-0.5 text-[11px] font-medium leading-snug ${CHIP_TONE[chip.tone]}`}
        >
          {chip.label}
        </span>
      ))}
    </div>
  );
}

function Block({
  title,
  zone,
  focus,
  children,
}: {
  title: string;
  /** undefined = never dimmed (summarises several sections). */
  zone?: PotentialFocus;
  focus: PotentialFocus | undefined;
  children: React.ReactNode;
}) {
  const emphasised = zone === undefined || focus === undefined || focus === zone;
  return (
    <div
      className="border-t border-gray-100 pt-2.5 first:border-t-0 first:pt-0 transition-opacity duration-300"
      style={{ opacity: emphasised ? 1 : DIMMED }}
    >
      <h4
        className={`text-[11px] font-bold uppercase tracking-wide mb-1.5 transition-colors duration-300 ${
          zone !== undefined && focus === zone ? "text-abis-accent" : "text-gray-500"
        }`}
      >
        {title}
      </h4>
      {children}
    </div>
  );
}

function Segments({ count, filled, color }: { count: number; filled: number | null; color: string }) {
  return (
    <div className="flex gap-1 mb-1.5">
      {Array.from({ length: count }, (_, i) => i + 1).map((segment) => (
        <span
          key={segment}
          className={`h-1.5 flex-1 rounded-full ${filled !== null && segment <= filled ? color : "bg-gray-200"}`}
        />
      ))}
    </div>
  );
}

/**
 * The live "Ihr Potential": what the answers add up to. Decorative in the
 * accessibility tree — every value is derived from a form field the visitor
 * just filled in, and is available as text there.
 */
export default function PotentialPanel({ potential, focus }: PotentialPanelProps) {
  const { stage, effort } = potential;
  return (
    <div aria-hidden="true" className="space-y-2.5">
      <Block title="Reifegrad" zone="datenbasis" focus={focus}>
        <Segments count={5} filled={stage.level} color={stage.level ? STAGE_COLOR[stage.level] : "bg-gray-200"} />
        <p className="text-[12px] font-semibold text-gray-800 leading-snug">
          {stage.level ? `Stufe ${stage.level} von 5 · ${stage.name}` : "Ergibt sich aus Abschnitt 02 und 04."}
        </p>
      </Block>
      <Block title="Datenlandschaft" zone="landschaft" focus={focus}>
        <Chips chips={potential.landscape} empty="Systeme, Berichte und Quellen in Abschnitt 01." />
      </Block>
      <Block title="Governance" zone="governance" focus={focus}>
        <Chips chips={potential.governance} empty="Berechtigungen und Vorgaben in Abschnitt 03." />
      </Block>
      <Block title="KI-Reife & Ziele" zone="ki" focus={focus}>
        <Chips chips={potential.ai} empty="KI-Einsatz und Ziele in Abschnitt 04." />
      </Block>
      <Block title="Empfohlener Pfad" zone="ziel" focus={focus}>
        {potential.path ? (
          <>
            <p className="text-[12px] font-semibold text-gray-800 leading-snug">{potential.path.label}</p>
            {potential.path.detail ? (
              <p className="text-[11px] text-gray-500 leading-snug mt-0.5">{potential.path.detail}</p>
            ) : null}
          </>
        ) : (
          <p className="text-[11px] text-gray-400 italic leading-snug">Vorhaben und Zielplattform in Abschnitt 05.</p>
        )}
      </Block>
      <Block title="Aufwandsband" focus={focus}>
        <Segments count={4} filled={effort.band} color={effort.band ? BAND_COLOR[effort.band] : "bg-gray-200"} />
        <p className="text-[12px] font-semibold text-gray-800 leading-snug">
          {effort.scored ? effort.bandName : "Noch keine Einschätzung"}
        </p>
      </Block>
      <Block title="Nächster Schritt" focus={focus}>
        <p className="text-[11px] text-gray-700 leading-snug">{potential.nextStep}</p>
      </Block>
    </div>
  );
}
